import { TrendingUp, Target, CheckCircle2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImprovementOpportunityProps {
  currentScore: number;
  currentAmount: number;
  medicareRevenue: number;
  lowScenarioInput: number;
  scalingFactor?: number;
  className?: string;
}

// Payment-adjustment math lives in the shared lib (R10 extract 2026-08-03)
import {
  computeAdjustmentPct as calculateAdjustment,
  MAX_PENALTY,
} from '@/lib/payment-adjustment';

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

export function ImprovementOpportunity({
  currentScore,
  currentAmount,
  medicareRevenue,
  lowScenarioInput,
  scalingFactor = 0.74,
  className
}: ImprovementOpportunityProps) {
  const roundedScore = Math.round(currentScore);
  const isInPenaltyZone = roundedScore < 75;
  const isInBonusZone = roundedScore >= 76;
  
  // Calculate various targets
  const breakEvenAmount = medicareRevenue * calculateAdjustment(75, lowScenarioInput, scalingFactor);
  const score85Amount = medicareRevenue * calculateAdjustment(85, lowScenarioInput, scalingFactor);
  const score100Amount = medicareRevenue * calculateAdjustment(100, lowScenarioInput, scalingFactor);

  // For penalty zone: show path to break-even and bonus
  if (isInPenaltyZone) {
    const penaltyEliminated = Math.abs(currentAmount);
    const score85Improvement = score85Amount - currentAmount;
    const maxImprovement = score100Amount - currentAmount;

    return (
      <div className={cn("w-full", className)}>
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
          <Target className="h-4 w-4 text-amber-500" />
          Improvement Opportunity
        </h3>
        
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
            Your score is currently in the <strong>penalty zone</strong>. Here's what improving could mean:
          </p>
          
          {/* Milestone Path */}
          <div className="space-y-3">
            {/* Current to Break-Even */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-[120px]">
                <div className="h-8 w-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xs font-bold text-red-600 dark:text-red-400">{roundedScore}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-400">75</span>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Break Even
                </p>
                <p className="text-xs text-muted-foreground">
                  Eliminate {formatCurrency(penaltyEliminated)} penalty
                </p>
              </div>
            </div>
            
            {/* Current to Score 85 */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-[120px]">
                <div className="h-8 w-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xs font-bold text-red-600 dark:text-red-400">{roundedScore}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">85</span>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Bonus Zone
                </p>
                <p className="text-xs text-muted-foreground">
                  Total swing: {formatCurrency(score85Improvement)}
                </p>
              </div>
            </div>
            
            {/* Current to Score 100 */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-[120px]">
                <div className="h-8 w-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xs font-bold text-red-600 dark:text-red-400">{roundedScore}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className="h-8 w-8 rounded-full bg-emerald-200 dark:bg-emerald-800 flex items-center justify-center">
                  <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">100</span>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Maximum Bonus
                </p>
                <p className="text-xs text-muted-foreground">
                  Total swing: {formatCurrency(maxImprovement)}
                </p>
              </div>
            </div>
          </div>
          
          {/* Total Opportunity Callout */}
          <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Total Opportunity (to max):
              </span>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(maxImprovement)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For bonus zone: show current achievement and room for growth
  if (isInBonusZone) {
    const additionalPotential = score100Amount - currentAmount;
    const atMaximum = roundedScore >= 100;
    
    // Calculate penalty avoided (what would have happened at score 0)
    const worstCasePenalty = medicareRevenue * MAX_PENALTY; // -9% penalty
    const penaltyAvoided = Math.abs(worstCasePenalty); // Convert to positive
    const totalValue = currentAmount + penaltyAvoided; // Bonus earned + penalty avoided

    return (
      <div className={cn("w-full", className)}>
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Current Achievement
        </h3>
        
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
          <p className="text-sm text-emerald-800 dark:text-emerald-200 mb-4">
            Congratulations! Your score is in the <strong>bonus zone</strong>.
          </p>
          
          {/* Three-column layout: Projected Bonus / Penalty Avoided / Total Opportunity */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800">
              <p className="text-xs text-muted-foreground mb-1">Projected Bonus</p>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(currentAmount)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                At current score
              </p>
            </div>
            
            <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-blue-100 dark:border-blue-800">
              <p className="text-xs text-muted-foreground mb-1">Penalty Avoided</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(penaltyAvoided).replace('+', '')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                vs. non-participation
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-100 to-blue-100 dark:from-emerald-900/30 dark:to-blue-900/30 rounded-lg p-3 border border-emerald-200 dark:border-emerald-700">
              <p className="text-xs text-muted-foreground mb-1">Total Opportunity</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-200">
                {formatCurrency(totalValue).replace('+', '')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Projected value
              </p>
            </div>
          </div>
          
          {!atMaximum && additionalPotential > 0 && (
            <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                    Additional potential:
                  </span>
                </div>
                <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(additionalPotential)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                If score improves from {roundedScore} to 100
              </p>
            </div>
          )}
          
          {atMaximum && (
            <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-700 text-center">
              <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                🎉 You're at maximum performance!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Neutral (score = 75)
  return (
    <div className={cn("w-full", className)}>
      <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
        <Target className="h-4 w-4 text-slate-500" />
        Improvement Opportunity
      </h3>
      
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
          You're at the <strong>break-even point</strong>. No penalty, but no bonus either.
        </p>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <span className="text-xs font-bold text-slate-600 dark:text-slate-400">75</span>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">100</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">
              Potential bonus: {formatCurrency(score100Amount)}
            </p>
            <p className="text-xs text-muted-foreground">
              Every point above 75 earns additional bonus
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

